'use client';

import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMultiClick } from '@/hooks/useMultiClick';
import MediaUploadModal from '@/components/MediaUploadModal';

const Gallery = (): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const handleMultiClick = useMultiClick(5, () => setShowUploadModal(true));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const images = [
    {
      src: 'https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/ahmad-ajmi-2Wqit9jCNeY-unsplash.jpg',
      category: 'Interior',
      title: 'Luxury Living Room',
      description: 'Beautifully designed living space with warm, inviting ambiance'
    },
    {
      src: 'https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/collov-home-design--aDGbdTsBZg-unsplash.jpg',
      category: 'Living Spaces',
      title: 'Modern Living Room',
      description: 'Stylish and spacious living area with contemporary furnishings'
    },
    {
      src: 'https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/shoham-avisrur-FgZIoKBfx08-unsplash%20(1).jpg',
      category: 'Exterior',
      title: 'Mountain View Villa Exterior',
      description: 'Stunning exterior showcasing the property\'s elegant architecture'
    },
    {
      src: 'https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/spacejoy-GQQyH0yNqLk-unsplash.jpg',
      category: 'Decor',
      title: 'Premium Decor',
      description: 'Premium furnishings and tasteful decor throughout the property'
    }
  ];

  return (
    <section id="gallery" ref={sectionRef} className="pt-20 pb-20 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#333333] mb-6">
              Property Gallery
            </h2>
            <p className="text-lg sm:text-xl text-[#333333]/80 max-w-3xl mx-auto leading-relaxed mb-8 px-4">
              Take a virtual tour through Mountain View's beautiful spaces and discover the luxury that awaits you
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 pb-16">
            {images.map((image, index) => (
              <div
                key={index}
                className="cursor-pointer group transition-all duration-500 hover:scale-105"
                onClick={() => { handleMultiClick(); setSelectedImage(image.src); }}
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500">
                  <div className="aspect-[4/3] w-full">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-white text-xl font-bold mb-2">{image.title}</h3>
                      <p className="text-white/90 text-sm mb-2">{image.description}</p>
                      <span className="inline-block bg-[#7C8F75] text-white px-3 py-1 rounded-full text-xs font-medium">
                        {image.category}
                      </span>
                    </div>
                  </div>

                  <div className="absolute top-4 left-4 bg-[#7C8F75]/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-white text-sm font-medium">{image.category}</span>
                  </div>

                  <div className="absolute inset-0 border-2 border-[#7C8F75] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <Button
              onClick={() => setSelectedImage(null)}
              variant="ghost"
              size="icon"
              className="absolute -top-12 right-0 text-white hover:bg-white/10 z-10"
            >
              <X className="h-6 w-6" />
            </Button>
            <img
              src={selectedImage}
              alt="Gallery image"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Wave transition to Amenities section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
        <svg 
          className="relative block w-full h-32" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="galleryWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
              <stop offset="20%" stopColor="rgba(255, 255, 255, 0.9)" stopOpacity="0.9" />
              <stop offset="40%" stopColor="rgba(249, 246, 242, 0.7)" stopOpacity="0.7" />
              <stop offset="60%" stopColor="rgba(249, 246, 242, 0.8)" stopOpacity="0.8" />
              <stop offset="80%" stopColor="rgba(249, 246, 242, 0.95)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="galleryWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.8)" stopOpacity="0.8" />
              <stop offset="30%" stopColor="rgba(124, 143, 117, 0.05)" stopOpacity="0.3" />
              <stop offset="60%" stopColor="rgba(232, 227, 221, 0.6)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#E8E3DD" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="galleryWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.6)" stopOpacity="0.6" />
              <stop offset="50%" stopColor="rgba(249, 246, 242, 0.9)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#F9F6F2" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave */}
          <path 
            d="M0,60L48,55C96,50,192,40,288,45C384,50,480,70,576,75C672,80,768,70,864,60C960,50,1056,40,1152,45C1200,50,1248,70,1296,75L1344,80L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#galleryWaveGradient1)"
          />
          
          {/* Second wave layer */}
          <path 
            d="M0,40L48,35C96,30,192,20,288,25C384,30,480,50,576,55C672,60,768,50,864,40C960,30,1056,20,1152,25C1200,30,1248,50,1296,55L1344,60L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#galleryWaveGradient2)"
          />
          
          {/* Final wave */}
          <path 
            d="M0,20L48,15C96,10,192,0,288,5C384,10,480,30,576,35C672,40,768,30,864,20C960,10,1056,0,1152,5C1200,10,1248,30,1296,35L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#galleryWaveGradient3)"
          />
        </svg>
      </div>

      <MediaUploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </section>
  );
};

export default Gallery;